import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Card,
  Button,
  AuthContext,
  ViewContext,
  Loader,
  Link,
  useAPI,
} from "components/lib";
import Axios from "axios";
import {
  V2_SETTINGS_URL,
  v2SettingsLinkAvailable,
} from "./client-onboarding-v2-settings.helpers";

// Mirrors the entity type VALUES in bin/onboarding-templates.js
// (MULTI_ENTITY_CHOICES / TRUST_FOCUSED_CHOICES) and the labels in
// helper/clientOnboarding.js ENTITY_TYPE_LABELS. A type missing here is not
// selectable, so it can never be enabled for an account.
//
// Shared across all three AUSTRAC templates, so "individual" stays listed even
// though the multi-entity form no longer offers it -- the trust-focused and
// individual-only templates still do.
const ENTITY_TYPES = [
  { value: "individual", label: "Individual / Sole Trader" },
  { value: "trust", label: "Trust" },
  { value: "unit_trust", label: "Unit Trust" },
  { value: "smsf", label: "Self-Managed Superannuation Fund (SMSF)" },
  { value: "body_corporate", label: "Company" },
  { value: "not_for_profit", label: "Not for Profit (Charity)" },
  { value: "partnership", label: "Partnership" },
  { value: "unincorporated_association", label: "Unincorporated Association" },
  { value: "government_body", label: "Government Body" },
  { value: "deceased_estate", label: "Deceased Estate" },
];

// What an account with no stored selection gets. Deliberately NOT every value in
// ENTITY_TYPES: the entity types added in the multi-entity review are opt-in, so
// they stay unticked until someone enables them. Note the server fails OPEN --
// helper/clientOnboarding.js allows everything when enabled_entity_types is absent
// -- so this default only bites once a selection has been saved.
const DEFAULT_ENABLED_ENTITY_TYPES = [
  "individual",
  "trust",
  "body_corporate",
  "partnership",
  "unincorporated_association",
  "government_body",
];

// Mirrors helper/xpmClientGroups.js for UI choices only — the server validates
// every override against the same list, so a stale copy here cannot send XPM an
// invalid <Type>. Same client/server mirroring as ENTITY_TYPES above.
const XPM_RELATIONSHIP_TYPES = [
  "Director",
  "Shareholder",
  "Trustee",
  "Beneficiary",
  "Partner",
  "Settlor",
  "Associate",
  "Secretary",
  "Public Officer",
  "Husband",
  "Wife",
  "Spouse",
  "Parent Of",
  "Child Of",
  "Appointer",
  "Member",
  "Auditor",
  "Owner",
];

// The roles the seeded multi-entity template offers, with the default XPM type
// each maps to when no override is set.
const RELATIONSHIP_ROLES = [
  { value: "trustee", label: "Is the Trustee of", default: "Trustee" },
  { value: "director", label: "Is a Director of", default: "Director" },
  { value: "shareholder", label: "Is a Shareholder of", default: "Shareholder" },
  { value: "partner", label: "Is a Partner in", default: "Partner" },
  {
    value: "beneficial_owner",
    label: "Is a Beneficial Owner of",
    default: "Owner",
  },
  { value: "appointor", label: "Is the Appointor of", default: "Appointer" },
  { value: "settlor", label: "Is the Settlor of", default: "Settlor" },
  { value: "protector", label: "Is the Protector of", default: "Associate" },
  {
    value: "office_holder",
    label: "Is an Office Holder of",
    default: "Associate",
  },
  { value: "other", label: "Other", default: "Associate" },
];

const TEMPLATES = [
  {
    value: "8e0d27a7-4215-53ea-94b6-3ffce2511d35",
    label: "Multi-Entity (All Types)",
  },
  {
    value: "4e1cd63b-6e3c-5267-bf0a-fdec23186b1d",
    label: "Trust-Focused (Individual + Trust + Company)",
  },
  {
    value: "f846be50-8808-596e-bcc9-f613fa9b0b22",
    label: "Individual Only",
  },
];

// Mirrors DEFAULT_CLIENT_GROUP_ENTITY_MAPPINGS in helper/xpmClientMappings.js,
// but UNLIKE XPM_RELATIONSHIP_TYPES above this copy is not re-validated server-side
// -- it WINS. buildXpmClientXml uses a stored field_mappings entry wholesale when
// it is non-empty, so whatever is saved here replaces the server defaults rather
// than merging over them. That is why these entries are deliberately narrower than
// the server's (no address block) and why saving this page drops those fields from
// XPM client creation. Pre-existing behaviour; see the PR notes.
const DEFAULT_FIELD_MAPPINGS = {
  individual: {
    Name: "{firstName} {lastName}",
    Email: "{email}",
    Phone: "{phone}",
    Mobile: "{mobile}",
    BusinessNumber: "{abn}",
    BusinessStructure: "Individual",
    FirstName: "{firstName}",
    LastName: "{lastName}",
    OtherName: "{middleName}",
    DateOfBirth: "{dob}",
    Address: "{residential_address}",
    City: "{suburb}",
    Region: "{state}",
    PostCode: "{postcode}",
    Country: "{country}",
  },
  trust: {
    Name: "{trust_name}",
    Email: "{contact_email}",
    Phone: "{phone}",
    BusinessStructure: "Trust",
    BusinessNumber: "{trust_abn}",
  },
  unit_trust: {
    Name: "{trust_name}",
    Email: "{contact_email}",
    Phone: "{phone}",
    BusinessStructure: "Trust",
    BusinessNumber: "{trust_abn}",
  },
  smsf: {
    Name: "{trust_name}",
    Email: "{contact_email}",
    Phone: "{phone}",
    BusinessStructure: "Trust",
    BusinessNumber: "{trust_abn}",
  },
  body_corporate: {
    Name: "{company_name}",
    Email: "{company_email}",
    Phone: "{phone}",
    BusinessStructure: "Company",
    CompanyNumber: "{company_acn}",
    BusinessNumber: "{company_abn}",
    Address: "{registered_address}",
  },
  not_for_profit: {
    Name: "{company_name}",
    Email: "{company_email}",
    Phone: "{phone}",
    BusinessStructure: "Company",
    CompanyNumber: "{company_acn}",
    BusinessNumber: "{company_abn}",
    Address: "{registered_address}",
  },
  partnership: {
    Name: "{partnership_name}",
    Email: "{partnership_email}",
    Phone: "{phone}",
    BusinessStructure: "Partnership",
    BusinessNumber: "{partnership_abn}",
  },
  unincorporated_association: {
    Name: "{association_name}",
    Email: "{association_email}",
    Phone: "{phone}",
    BusinessStructure: "Other",
    BusinessNumber: "{association_abn}",
  },
  government_body: {
    Name: "{gov_entity_name}",
    Email: "{gov_contact_email}",
    Phone: "{phone}",
    BusinessStructure: "Other",
    BusinessNumber: "{gov_abn}",
  },
  deceased_estate: {
    Name: "{estate_name}",
    Email: "{estate_contact_email}",
    Phone: "{phone}",
    BusinessStructure: "Trust",
    BusinessNumber: "{estate_abn}",
  },
};

// What the XPM destination fields on the Field Mappings tab actually are. The
// names are XPM's element names, and two of them are easy to read backwards:
// BusinessNumber is the ABN, not "a business number of some kind", and XPM's Tax
// Number is NOT reachable from this table at all.
const XPM_FIELD_GLOSSARY = [
  ["Name", "The client name as it appears in XPM."],
  ["BusinessStructure", "XPM's client type: Individual, Trust, Company, Partnership or Other."],
  ["BusinessNumber", "The entity's ABN (11 digits). This is XPM's Business Number field."],
  ["CompanyNumber", "The entity's ACN (9 digits). Companies only."],
  ["FirstName / LastName / OtherName", "Individuals only. XPM keeps these beside the full Name."],
  ["DateOfBirth", "Individuals only. Sent to XPM as YYYYMMDD."],
  ["Address / City / Region / PostCode / Country", "The entity's address block."],
  ["Email / Phone / Mobile", "Contact details on the XPM client record."],
];

// What the two number fields mean for each entity type, in the firm's language
// rather than XPM's. Written per type because "the ABN" and "the TFN" refer to a
// different legal person in each one -- the trustee's ABN is not the trust's.
const ENTITY_NUMBER_NOTES = {
  individual: {
    business: "The sole trader's own ABN. Leave blank for a private individual who does not trade.",
    tax: "The individual's personal TFN.",
  },
  trust: {
    business: "The trust's ABN — not the trustee's.",
    tax: "The trust's own TFN, which is separate from the trustee's.",
  },
  unit_trust: {
    business: "The unit trust's ABN — not the trustee's or a unitholder's.",
    tax: "The unit trust's own TFN.",
  },
  smsf: {
    business: "The fund's ABN, as registered on Super Fund Lookup.",
    tax: "The fund's own TFN — not a member's.",
  },
  body_corporate: {
    business: "The company's ABN. Its ACN goes in CompanyNumber.",
    tax: "The company's TFN — not a director's.",
  },
  not_for_profit: {
    business: "The organisation's ABN. Its ACN, if incorporated, goes in CompanyNumber.",
    tax: "The organisation's TFN.",
  },
  partnership: {
    business: "The partnership's ABN — not a partner's.",
    tax: "The partnership's own TFN, which is separate from each partner's.",
  },
  unincorporated_association: {
    business: "The association's ABN, if it has registered for one.",
    tax: "The association's TFN, if it has one.",
  },
  government_body: {
    business: "The agency's ABN.",
    tax: "The agency's TFN, where one applies.",
  },
  deceased_estate: {
    business: "The estate's ABN, if one was obtained for it.",
    tax: "The estate's own TFN, which is issued separately from the deceased person's.",
  },
};

/*
 * Fold a legacy `TaxNumber` key into `BusinessNumber`. Mirrors
 * collapseXpmTaxNumberKey in helper/clientOnboarding.js, which is the copy that
 * matters -- the server collapses on save AND on the way to XPM. This one only
 * stops the stale row rendering, so a firm is not editing a box whose value the
 * server is about to fold away.
 *
 * `TaxNumber` was the default key for Trust, Company and Partnership until
 * 2026-07-08. The builders have always renamed it to the <BusinessNumber>
 * element, so the two are the same destination; the old spelling just reads like
 * a tax file number, which it never was.
 */
const collapseTaxNumberKey = (map) => {
  if (!map || !Object.prototype.hasOwnProperty.call(map, "TaxNumber")) return map;
  const { TaxNumber, ...rest } = map;
  if (!String(TaxNumber || "").trim()) return rest;
  if (!String(rest.BusinessNumber || "").trim())
    return { ...rest, BusinessNumber: TaxNumber };
  // Two different templates for one field: keep both so a human decides.
  return rest.BusinessNumber === TaxNumber ? rest : map;
};

const mergeFieldMappings = (stored = {}) =>
  Object.fromEntries(
    Object.entries(DEFAULT_FIELD_MAPPINGS).map(([entityType, defaults]) => [
      entityType,
      collapseTaxNumberKey({
        ...defaults,
        ...(stored[entityType] || {}),
      }),
    ])
  );

export function ClientOnboardingSettingsView() {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const settingsAPI = useAPI("/api/client-onboarding-settings");
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);

  // Settings state
  const [enabled, setEnabled] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(
    "8e0d27a7-4215-53ea-94b6-3ffce2511d35"
  );
  const [enabledEntityTypes, setEnabledEntityTypes] = useState(
    DEFAULT_ENABLED_ENTITY_TYPES
  );
  const [checkType, setCheckType] = useState(
    "kyc+tranche2_v1+sanctions+adverse_media"
  );
  const [pmsProvider, setPmsProvider] = useState("");
  const [autoCreateEntities, setAutoCreateEntities] = useState(false);
  const [autoVerifyIndividuals, setAutoVerifyIndividuals] = useState(false);
  const [groupNameField, setGroupNameField] = useState("");
  const [addRelationships, setAddRelationships] = useState(false);
  const [writeTfn, setWriteTfn] = useState(false);
  const [relationshipTypeMap, setRelationshipTypeMap] = useState({});
  const [hideRelationshipsPage, setHideRelationshipsPage] = useState(false);
  const [hideGroupName, setHideGroupName] = useState(false);
  const [formLayout, setFormLayout] = useState(null);
  const [fieldMappings, setFieldMappings] = useState(
    mergeFieldMappings(DEFAULT_FIELD_MAPPINGS)
  );
  const [showV2Link, setShowV2Link] = useState(false);

  // Client Onboarding v2 has its own settings page. Offer it to an account that
  // can use it. The link is a convenience, so a failed read stays silent and
  // never holds up this page.
  useEffect(() => {
    let cancelled = false;
    Axios.get(V2_SETTINGS_URL)
      .then((res) => {
        if (!cancelled) setShowV2Link(v2SettingsLinkAvailable(res.data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load settings
  useEffect(() => {
    if (!settingsAPI.loading && settingsAPI.data) {
      const settings = settingsAPI.data;
      const data = settings.data || {};
      setEnabled(settings.enabled || false);
      setNotificationEmail(data.notification_email || "");
      setRedirectUrl(data.redirect_url || "");
      setEnabledEntityTypes(
        data.enabled_entity_types || DEFAULT_ENABLED_ENTITY_TYPES
      );
      setCheckType(
        data.verification?.check_type ||
          "kyc+tranche2_v1+sanctions+adverse_media"
      );
      setPmsProvider(data.pms_sync?.provider || "");
      setAutoCreateEntities(data.pms_sync?.auto_create === true);
      setAutoVerifyIndividuals(
        data.pms_sync?.auto_create === true &&
          data.pms_sync?.auto_verify_individuals === true
      );
      setGroupNameField(
        data.pms_sync?.auto_create === true
          ? data.pms_sync?.group_name_field || ""
          : ""
      );
      setAddRelationships(
        data.pms_sync?.auto_create === true &&
          data.pms_sync?.add_relationships === true
      );
      setRelationshipTypeMap(data.pms_sync?.relationship_type_map || {});
      setWriteTfn(
        data.pms_sync?.auto_create === true &&
          data.pms_sync?.write_tfn?.enabled === true
      );
      setHideRelationshipsPage(data.hide_relationships_page === true);
      setHideGroupName(data.hide_group_name === true);
      setFormLayout(settings.form_layout || null);
      setFieldMappings(mergeFieldMappings(data.pms_sync?.field_mappings));
      if (settings.misc?.form_id) {
        setSelectedTemplate(settings.misc.form_id);
      }
    }
  }, [settingsAPI.loading, settingsAPI.data]);

  const save = async () => {
    setSaving(true);
    try {
      await Axios.post("/api/client-onboarding-settings", {
        enabled,
        name: "Client Group Onboarding",
        data: {
          enabled,
          notification_email: notificationEmail,
          redirect_url: redirectUrl,
          entity_panel_name: "entities",
          entity_type_field: "entity_type",
          enabled_entity_types: enabledEntityTypes,
          hide_relationships_page: hideRelationshipsPage,
          hide_group_name: hideGroupName,
          verification: {
            mode: "manual",
            auto_trigger: false,
            live_mode: false,
            check_type: checkType,
          },
          pms_sync: {
            provider: pmsProvider,
            mode: "manual",
            auto_create: pmsProvider === "xpm" && autoCreateEntities,
            auto_verify_individuals:
              pmsProvider === "xpm" &&
              autoCreateEntities &&
              autoVerifyIndividuals,
            group_name_field:
              pmsProvider === "xpm" && autoCreateEntities
                ? groupNameField.trim()
                : "",
            add_relationships:
              pmsProvider === "xpm" && autoCreateEntities && addRelationships,
            relationship_type_map:
              pmsProvider === "xpm" && autoCreateEntities
                ? relationshipTypeMap
                : {},
            // Posted on EVERY save, and not only when it is on. The integration's
            // `data` jsonb is replaced wholesale on update, so a key the page
            // omits is destroyed -- which is the bug pms_sync.group_taxable has.
            write_tfn: {
              enabled:
                pmsProvider === "xpm" && autoCreateEntities && writeTfn,
            },
            field_mappings: fieldMappings,
          },
        },
        misc: {
          form_id: selectedTemplate,
        },
      });
      viewContext.notification.show("Settings saved", "success", true);
    } catch (err) {
      viewContext.notification.show("Failed to save settings", "error", true);
    } finally {
      setSaving(false);
    }
  };

  // Both directions of drift between what this account's FORM offers and what its
  // settings enable. Driven off the live form reported by the API, never off a
  // copy of the catalog: the account's form is an independent copy whose id is
  // not a catalog id and whose contents can predate the current templates.
  const formEntityTypes = formLayout?.entity_types || null;

  // Offered by the form but switched off: stripped from the served form and
  // rejected on submit.
  const offeredButDisabled = (formEntityTypes || []).filter(
    (type) => !enabledEntityTypes.includes(type)
  );

  // Switched on but absent from the form: almost always an older installed copy
  // that predates the type, so ticking it can never make it appear.
  const enabledButMissing = formEntityTypes
    ? enabledEntityTypes.filter((type) => !formEntityTypes.includes(type))
    : [];

  const labelFor = (type) =>
    ENTITY_TYPES.find((t) => t.value === type)?.label || type;

  const toggleEntityType = (type) => {
    setEnabledEntityTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const updateRelationshipType = (role, value) => {
    setRelationshipTypeMap((prev) => {
      const next = { ...prev };
      const fallback = RELATIONSHIP_ROLES.find((r) => r.value === role)?.default;

      if (value === "__skip__") {
        next[role] = null;
      } else if (value === fallback) {
        // Storing only real overrides keeps the saved map minimal, so a future
        // change to a default is picked up rather than frozen in settings.
        delete next[role];
      } else {
        next[role] = value;
      }
      return next;
    });
  };

  const updateFieldMapping = (entityType, pmsField, value) => {
    setFieldMappings((prev) => ({
      ...prev,
      [entityType]: {
        ...prev[entityType],
        [pmsField]: value,
      },
    }));
  };

  if (settingsAPI.loading) return <Loader />;

  const integrationId = settingsAPI.data?.id;
  const publicUrl = integrationId
    ? `${window.location.origin}/client-onboarding/${integrationId}`
    : null;
  // Opting the firm in to sending tax file numbers to Xero is an admin decision.
  // The server carries the stored value forward for everyone else, so this only
  // decides whether the control is offered -- it is not the check that matters.
  const mayWriteTfn = authContext.permission?.admin === true;
  const tfnAudit = settingsAPI.data?.tfn_audit;
  const questionNames = settingsAPI.data?.question_names || [];

  const tabs = [
    { key: "general", label: "General" },
    { key: "automation", label: "Automation" },
    { key: "entity_types", label: "Entity Types" },
    { key: "field_mappings", label: "Field Mappings" },
  ];

  return (
    <Grid>
      <h1 className="text-2xl font-bold">Client Onboarding Settings</h1>

      {showV2Link && (
        <div className="rounded border border-sg-blue-line bg-sg-blue-05 px-3 py-2 text-sm text-sg-blue-ink">
          Client Onboarding v2 is available for this account. It has its own
          link and settings, beside this form.{" "}
          <Link
            url="/client-onboarding-v2-settings"
            text="Open Client Onboarding v2 settings"
          />
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-brand-500 text-sg-blue"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === "general" && (
        <Card>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Enable Onboarding Form</h3>
                <p className="text-sm text-slate-500">
                  Allow clients to access the onboarding form via the public URL
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>

            {publicUrl && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Public URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={publicUrl}
                    className="flex-1 border border-slate-300 rounded-control px-3 py-2 text-sm bg-slate-50"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl);
                      viewContext.notification.show(
                        "URL copied",
                        "success",
                        true
                      );
                    }}
                    className="px-3 py-2 bg-btn-primary text-white rounded-control text-sm hover:bg-btn-primary-hover"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Notification Email
              </label>
              <input
                type="email"
                value={notificationEmail}
                onChange={(e) => setNotificationEmail(e.target.value)}
                placeholder="admin@yourfirm.com"
                className="w-full border border-slate-300 rounded-control px-3 py-2 text-sm"
              />
              <p className="text-xs text-slate-400 mt-1">
                Receives notification when a new onboarding form is submitted
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Redirect URL (after submission)
              </label>
              <input
                type="text"
                value={redirectUrl}
                onChange={(e) => setRedirectUrl(e.target.value)}
                placeholder="/thank-you"
                className="w-full border border-slate-300 rounded-control px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Form Template
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full border border-slate-300 rounded-control px-3 py-2 text-sm"
              >
                {TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">
                Select a template to use as the starting point for your onboarding
                form. You can customise it further in the Form Builder.
              </p>
            </div>

            <div className="rounded border border-slate-200 px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium">TFN Capture</h3>
                  <p className="text-sm text-slate-500">
                    Checks the saved onboarding form for TFN questions.
                  </p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tfnAudit?.has_tfn_fields
                      ? "bg-sg-green-bg text-sg-green"
                      : "bg-sg-orange-bg text-sg-orange"
                  }`}
                >
                  {tfnAudit?.has_tfn_fields ? "Detected" : "Not detected"}
                </span>
              </div>
              {tfnAudit?.questions?.length > 0 ? (
                <>
                  <ul className="mt-3 space-y-1 text-xs text-slate-600">
                    {tfnAudit.questions.map((question) => (
                      <li key={question.path}>
                        <span className="font-mono">{question.name}</span>
                        {question.tfn ? " · validation flag set" : " · name match"}
                        {question.writable ? (
                          <span className="text-sg-green">
                            {" "}
                            · can be written to XPM
                          </span>
                        ) : (
                          <span className="text-gray-400">
                            {" "}
                            · secure link only
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 mt-2">
                    Every TFN above is removed from the stored response and sent to
                    you in one secure link. Only a question asked on an
                    entity&apos;s own panel can also be written to that
                    entity&apos;s XPM Tax Number field &mdash; a TFN asked inside a
                    sub-panel belongs to someone who becomes an XPM contact, and a
                    contact has no tax number field.
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-400 mt-3">
                  Add fields named tfn or ending in _tfn and set the TFN flag in
                  the form builder before collecting TFNs.
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Automation Tab */}
      {activeTab === "automation" && (
        <Card>
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-4">Verification</h3>
              <div className="mb-4 rounded border border-sg-blue-line bg-sg-blue-05 px-3 py-2 text-sm text-sg-blue-ink">
                Client onboarding runs in manual review mode by default. If XPM
                auto-create and auto-verify are both enabled, individual checks
                are triggered after the submission creates entity records.
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Verification Check Type
                </label>
                <select
                  value={checkType}
                  onChange={(e) => setCheckType(e.target.value)}
                  className="w-full border border-slate-300 rounded-control px-3 py-2 text-sm"
                >
                  <option value="kyc">KYC Only</option>
                  <option value="kyc+sanctions">KYC + PEP/Sanctions</option>
                  <option value="kyc+sanctions+adverse_media">
                    KYC + PEP/Sanctions + Adverse Media
                  </option>
                  <option value="kyc+tranche2_v1">KYC + Tranche 2</option>
                  <option value="kyc+tranche2_v1+sanctions">
                    KYC + Tranche 2 + PEP/Sanctions
                  </option>
                  <option value="kyc+tranche2_v1+sanctions+adverse_media">
                    KYC + Tranche 2 + PEP/Sanctions + Adverse Media
                  </option>
                </select>
              </div>
            </div>

            <hr />

            <div>
              <h3 className="font-medium mb-4">
                Practice Management System (PMS) Sync
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Provider
                </label>
                <select
                  value={pmsProvider}
                  onChange={(e) => {
                    setPmsProvider(e.target.value);
                    if (e.target.value !== "xpm") {
                      setAutoCreateEntities(false);
                      setAutoVerifyIndividuals(false);
                    }
                  }}
                  className="w-full border border-slate-300 rounded-control px-3 py-2 text-sm"
                >
                  <option value="">None</option>
                  <option value="xpm">XPM (Xero Practice Manager)</option>
                  <option value="karbon" disabled>
                    Karbon (Coming Soon)
                  </option>
                </select>
              </div>

              {pmsProvider && (
                <div className="space-y-4">
                  <div className="rounded border border-sg-blue-line bg-sg-blue-05 px-3 py-2 text-sm text-sg-blue-ink">
                    Staff can sync unsynced entities from the client group
                    review screen. Auto-create queues XPM client creation for
                    top-level entities after submission, with nested individuals
                    created as contacts.
                  </div>

                  {pmsProvider === "xpm" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Auto-create entities</h4>
                          <p className="text-sm text-slate-500">
                            Queue XPM client creation for each top-level
                            submitted entity.
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={autoCreateEntities}
                            onChange={(e) => {
                              setAutoCreateEntities(e.target.checked);
                              if (!e.target.checked) {
                                setAutoVerifyIndividuals(false);
                                setGroupNameField("");
                                setAddRelationships(false);
                                setWriteTfn(false);
                              }
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                      </div>

                      {autoCreateEntities && (
                        <div className="flex items-center justify-between rounded border border-slate-200 px-3 py-3">
                          <div>
                            <h4 className="font-medium">
                              Auto-verify individuals
                            </h4>
                            <p className="text-sm text-slate-500">
                              Send verification emails to individual records
                              created from the submission.
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoVerifyIndividuals}
                              onChange={(e) =>
                                setAutoVerifyIndividuals(e.target.checked)
                              }
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                          </label>
                        </div>
                      )}

                      {autoCreateEntities && (
                        <div className="rounded border border-slate-200 px-3 py-3">
                          <h4 className="font-medium">Client group name field</h4>
                          <p className="text-sm text-slate-500 mb-2">
                            The form question whose answer names the XPM client
                            group. Leave blank to skip client group creation.
                          </p>
                          {questionNames.length > 0 ? (
                            <select
                              value={groupNameField}
                              onChange={(e) => setGroupNameField(e.target.value)}
                              className="w-full border border-slate-300 rounded px-3 py-2"
                            >
                              <option value="">
                                Don't create an XPM client group
                              </option>
                              {/* A saved field the current form no longer has
                                  would otherwise render as nothing selected,
                                  hiding a live setting from the admin. */}
                              {groupNameField &&
                                !questionNames.some(
                                  (q) => q.name === groupNameField
                                ) && (
                                  <option value={groupNameField}>
                                    {groupNameField} (not in the current form)
                                  </option>
                                )}
                              {questionNames.map((q) => (
                                <option key={q.name} value={q.name}>
                                  {q.title} ({q.name})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={groupNameField}
                              onChange={(e) => setGroupNameField(e.target.value)}
                              placeholder="group_name"
                              className="w-full border border-slate-300 rounded px-3 py-2"
                            />
                          )}
                        </div>
                      )}

                      {autoCreateEntities && (
                        <div className="rounded border border-slate-200 px-3 py-3">
                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <h4 className="font-medium">
                                Write tax numbers to XPM
                              </h4>
                              <p className="text-sm text-gray-500">
                                Write each entity&apos;s TFN into the XPM
                                client&apos;s Tax Number field, as a separate
                                update after the client is created. A tax number
                                XPM rejects &mdash; a duplicate, most often
                                &mdash; is reported on the entity and leaves the
                                rest of the client record untouched.
                              </p>
                              {!mayWriteTfn && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Only an administrator can change this setting.
                                </p>
                              )}
                            </div>
                            <label
                              className={`relative inline-flex items-center ${
                                mayWriteTfn ? "cursor-pointer" : "cursor-not-allowed"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={writeTfn}
                                disabled={!mayWriteTfn}
                                onChange={(e) => setWriteTfn(e.target.checked)}
                                className="sr-only peer"
                              />
                              <div
                                className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full ${
                                  mayWriteTfn ? "" : "opacity-50"
                                }`}
                              ></div>
                            </label>
                          </div>

                          {writeTfn && !tfnAudit?.writable_count && (
                            <p className="text-sm text-sg-orange mt-3">
                              {tfnAudit?.has_tfn_fields
                                ? "Your form asks for a TFN, but not on any entity's own panel — every one it asks for belongs to someone who becomes an XPM contact, and a contact has no tax number field. Nothing can be written."
                                : "Your current form asks no TFN question, so there is nothing to write."}{" "}
                              Add a field named{" "}
                              <span className="font-mono">tfn</span> or ending in{" "}
                              <span className="font-mono">_tfn</span> to each
                              entity panel in the Form Builder.
                            </p>
                          )}
                        </div>
                      )}

                      {autoCreateEntities && (
                        <div className="rounded border border-gray-200 px-3 py-3">
                          <div className="flex items-center justify-between">
                            <div className="pr-4">
                              <h4 className="font-medium">
                                Add client relationships
                              </h4>
                              <p className="text-sm text-slate-500">
                                Write the Relationships page into XPM. Only
                                relationships between top-level entities can be
                                written — people entered inside an entity
                                (directors, trustees, partners) become XPM
                                contacts, and a contact cannot be a relationship
                                endpoint.
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={addRelationships}
                                onChange={(e) =>
                                  setAddRelationships(e.target.checked)
                                }
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                            </label>
                          </div>

                          {addRelationships && (
                            <div className="mt-4 border-t border-slate-100 pt-3">
                              <p className="text-sm text-slate-500 mb-2">
                                XPM relationship type for each role. Defaults are
                                shown; three have no exact XPM equivalent and are
                                mapped to the closest available.
                              </p>
                              <div className="space-y-2">
                                {RELATIONSHIP_ROLES.map((role) => (
                                  <div
                                    key={role.value}
                                    className="flex items-center justify-between gap-3"
                                  >
                                    <span className="text-sm text-slate-700">
                                      {role.label}
                                    </span>
                                    <select
                                      value={
                                        relationshipTypeMap[role.value] === null
                                          ? "__skip__"
                                          : relationshipTypeMap[role.value] ||
                                            role.default
                                      }
                                      onChange={(e) =>
                                        updateRelationshipType(
                                          role.value,
                                          e.target.value
                                        )
                                      }
                                      className="border border-slate-300 rounded px-2 py-1 text-sm"
                                    >
                                      {XPM_RELATIONSHIP_TYPES.map((type) => (
                                        <option key={type} value={type}>
                                          {type}
                                        </option>
                                      ))}
                                      <option value="__skip__">
                                        Skip (don't sync)
                                      </option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Entity Types Tab */}
      {activeTab === "entity_types" && (
        <Card>
          <h3 className="font-medium mb-4">Enabled Entity Types</h3>
          <p className="text-sm text-slate-500 mb-4">
            Select which entity types clients can add in the onboarding form.
          </p>
          {enabledButMissing.length > 0 && (
            <div className="mb-4 p-3 rounded bg-sg-orange-bg text-sg-orange text-sm">
              <p className="font-medium">
                Your current form does not have these entity types.
              </p>
              <p className="mt-1">
                {enabledButMissing.map(labelFor).join(", ")} are switched on, but
                your form was installed before they existed, so they cannot
                appear no matter what is ticked here. Re-select your form
                template under <strong>General</strong> and save to take a fresh
                copy.
              </p>
            </div>
          )}
          {offeredButDisabled.length > 0 && (
            <div className="mb-4 p-3 rounded bg-sg-orange-bg text-sg-orange text-sm">
              <p className="font-medium">
                Your form offers types that are switched off.
              </p>
              <p className="mt-1">
                {offeredButDisabled.map(labelFor).join(", ")} will not appear on
                your public form, and a client who somehow submits one is
                rejected. Tick them below to offer them.
              </p>
              <Button
                small
                text="Enable all"
                action={() =>
                  setEnabledEntityTypes((prev) => [
                    ...prev,
                    ...offeredButDisabled,
                  ])
                }
              />
            </div>
          )}
          <div className="space-y-3">
            {ENTITY_TYPES.map((type) => (
              <label
                key={type.value}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={enabledEntityTypes.includes(type.value)}
                  onChange={() => toggleEntityType(type.value)}
                  className="w-4 h-4 text-sg-blue rounded border-slate-300"
                />
                <span className="text-sm">{type.label}</span>
              </label>
            ))}
          </div>

          <h3 className="font-medium mb-4 mt-8">Form Sections</h3>
          <p className="text-sm text-gray-500 mb-4">
            Optional parts of the onboarding form. Everything here is shown by
            default; switch a section off if your firm does not collect it.
          </p>

          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={hideGroupName}
              onChange={() => setHideGroupName((prev) => !prev)}
              className="w-4 h-4 mt-1 text-sg-blue rounded border-gray-300"
            />
            <span className="text-sm">
              Do not ask the client to name their client group
              <span className="block text-gray-500">
                Use this when your firm assigns client groups itself. The group
                is created under the primary contact&apos;s name and flagged as
                needing a name, and no XPM client group is created until staff
                rename it.
              </span>
              {hideGroupName && formLayout?.has_group_name_question === false && (
                <span className="block text-sg-orange mt-1">
                  Your current form does not ask for a client group name, so this
                  setting changes nothing.
                </span>
              )}
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={hideRelationshipsPage}
              onChange={() => setHideRelationshipsPage((prev) => !prev)}
              className="w-4 h-4 mt-1 text-sg-blue rounded border-gray-300"
            />
            <span className="text-sm">
              Hide the relationships page
              <span className="block text-gray-500">
                Shortens the form, but XPM client relationships can only be
                written from the answers it collects. Beneficial ownership is
                never hidden.
              </span>
              {hideRelationshipsPage &&
                formLayout &&
                formLayout.has_relationships_page &&
                !formLayout.has_beneficial_ownership_page && (
                  <span className="block text-sg-orange mt-1">
                    <strong>This setting is not taking effect.</strong> Your
                    current form asks the beneficial ownership question inside
                    the relationships page, so hiding the page would remove an
                    AUSTRAC-required question &mdash; the page is still being
                    shown. Re-select the form template above to get the newer
                    layout, where beneficial ownership has its own page.
                  </span>
                )}
            </span>
          </label>
        </Card>
      )}

      {/* Field Mappings Tab */}
      {activeTab === "field_mappings" && (
        <Card>
          <h3 className="font-medium mb-2">PMS Field Mappings</h3>
          <p className="text-sm text-slate-500 mb-4">
            Configure how form fields map to PMS fields. The left-hand name is the
            XPM field the value is written to; the box holds a template, where{" "}
            <code className="bg-slate-100 px-1 rounded">{"{fieldName}"}</code>{" "}
            is replaced by that question&apos;s answer for the entity.
          </p>

          {pmsProvider && (
            <div className="mb-6 rounded border border-gray-200 px-3 py-3">
              <h4 className="text-sm font-medium mb-2">What these XPM fields are</h4>
              <dl className="space-y-1">
                {XPM_FIELD_GLOSSARY.map(([field, meaning]) => (
                  <div key={field} className="flex gap-3 text-xs">
                    <dt className="w-64 font-mono text-gray-500">{field}</dt>
                    <dd className="flex-1 text-gray-600">{meaning}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-3 rounded bg-sg-blue-05 border border-sg-blue-line px-3 py-2 text-xs text-sg-blue-ink">
                <strong>Tax numbers are not set here.</strong> In XPM,{" "}
                <span className="font-mono">BusinessNumber</span> is the{" "}
                <strong>ABN</strong>, and the separate{" "}
                <strong>Tax Number</strong> field holds the <strong>TFN</strong>. A
                TFN is never written through this table &mdash; anything mapped here
                would be filed as a business number. Turn on{" "}
                <strong>Write tax numbers to XPM</strong> on the Automation tab
                instead; it writes the TFN on its own, after the client record is
                created, so a rejected tax number cannot cost you the rest of the
                record.
              </div>
            </div>
          )}

          {pmsProvider ? (
            Object.entries(fieldMappings).map(([entityType, mappings]) => (
              <div key={entityType} className="mb-6">
                <h4 className="text-sm font-medium text-sg-blue mb-2">
                  {labelFor(entityType)}
                </h4>
                {ENTITY_NUMBER_NOTES[entityType] && (
                  <dl className="mb-2 space-y-1">
                    <div className="flex gap-3 text-xs">
                      <dt className="w-32 font-mono text-gray-500">
                        BusinessNumber
                      </dt>
                      <dd className="flex-1 text-gray-600">
                        {ENTITY_NUMBER_NOTES[entityType].business}
                      </dd>
                    </div>
                    <div className="flex gap-3 text-xs">
                      <dt className="w-32 text-gray-400">Tax Number</dt>
                      <dd className="flex-1 text-gray-400">
                        {ENTITY_NUMBER_NOTES[entityType].tax}{" "}
                        <span className="italic">
                          Written from the Automation tab, not from this table.
                        </span>
                      </dd>
                    </div>
                  </dl>
                )}
                <div className="space-y-2">
                  {Object.entries(mappings).map(([pmsField, formField]) => (
                    <div key={pmsField} className="flex gap-2 items-center">
                      <span className="w-40 text-xs text-slate-500 font-mono">
                        {pmsField}
                      </span>
                      <input
                        type="text"
                        value={formField}
                        onChange={(e) =>
                          updateFieldMapping(
                            entityType,
                            pmsField,
                            e.target.value
                          )
                        }
                        className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm font-mono"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() =>
                    setFieldMappings((prev) => ({
                      ...prev,
                      [entityType]: DEFAULT_FIELD_MAPPINGS[entityType] || {},
                    }))
                  }
                  className="text-xs text-sg-blue hover:underline mt-1"
                >
                  Reset to defaults
                </button>
              </div>
            ))
          ) : (
            <p className="text-sm text-slate-400">
              Select a PMS provider in the Automation tab to configure field
              mappings.
            </p>
          )}
        </Card>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          text={saving ? "Saving..." : "Save Settings"}
          action={save}
          disabled={saving}
        />
      </div>
    </Grid>
  );
}

export default ClientOnboardingSettingsView;
